<template>
    <div class="po-dash-nav-wrap">
        <div class="po-bottom-nav d-lg-none">
            <router-link
                v-for="item in mobileSlots"
                :key="'m-' + item.id"
                :to="item.href"
                class="po-bottom-nav__item"
                :class="{ 'po-bottom-nav__item': item.accent }"
                :title="item.label"
            >
                <i class="bi po-bottom-nav__ico" :class="mobileIcon(item)" aria-hidden="true"></i>
                <span>{{ item.shortLabel || item.label }}</span>
                <span v-if="item.showPedidosBadge && pedidosNuevosBadge" class="po-nav-badge">{{ pedidosNuevosBadge }}</span>
            </router-link>
            <button
                type="button"
                class="po-bottom-nav__item po-bottom-nav__item--btn"
                :class="{ 'po-bottom-nav__item--open': moreOpen }"
                title="Más"
                aria-haspopup="dialog"
                :aria-expanded="moreOpen ? 'true' : 'false'"
                @click="moreOpen = !moreOpen"
            >
                <i class="bi bi-grid-fill po-bottom-nav__ico" aria-hidden="true"></i>
                <span>Más</span>
            </button>
        </div>

        <Teleport to="body">
            <div
                v-if="moreOpen"
                class="po-nav-more d-lg-none"
                role="dialog"
                aria-modal="true"
                aria-labelledby="poNavMoreTitle"
                @keydown.esc="cerrarMore"
            >
                <button type="button" class="po-nav-more__backdrop" aria-label="Cerrar" @click="cerrarMore" />
                <div class="po-nav-more__sheet">
                    <div class="po-nav-more__head">
                        <h2 id="poNavMoreTitle" class="po-nav-more__title">Más accesos</h2>
                        <button type="button" class="btn-close btn-close-white" aria-label="Cerrar" @click="cerrarMore" />
                    </div>
                    <div class="po-nav-more__body">
                        <template v-for="sec in moreSections" :key="sec.id">
                            <p v-if="sec.items.length" class="po-nav-more__section">{{ sec.title }}</p>
                            <div v-if="sec.items.length" class="po-nav-more__grid">
                                <router-link
                                    v-for="item in sec.items"
                                    :key="'more-' + item.id"
                                    :to="item.href"
                                    class="po-nav-more__link"
                                    @click="onMoreLinkClick($event, item)"
                                >
                                    <i class="bi" :class="item.icon" aria-hidden="true"></i>
                                    <span>{{ item.label }}</span>
                                    <span v-if="item.showPedidosBadge && pedidosNuevosBadge" class="badge rounded-pill text-bg-danger">{{ pedidosNuevosBadge }}</span>
                                </router-link>
                            </div>
                        </template>
                    </div>
                    <div class="po-nav-more__foot">
                        <button
                            type="button"
                            class="po-nav-more__logout"
                            data-bs-toggle="modal"
                            data-bs-target="#mainLogoutModal"
                            @click="cerrarMore"
                        >
                            <i class="bi bi-box-arrow-right" aria-hidden="true"></i>
                            <span>Cerrar sesión</span>
                        </button>
                    </div>
                </div>
            </div>
        </Teleport>

        <aside
            class="po-sidebar d-none d-lg-flex flex-column"
            :class="{ 'po-sidebar--expanded': sidebarExpanded }"
        >
            <button
                type="button"
                class="po-sidebar__toggle"
                :aria-expanded="sidebarExpanded ? 'true' : 'false'"
                :aria-label="sidebarExpanded ? 'Contraer menú' : 'Expandir menú'"
                :title="sidebarExpanded ? 'Contraer menú' : 'Expandir menú'"
                @click="toggleSidebarExpanded"
            >
                <i class="bi" :class="sidebarExpanded ? 'bi-chevron-left' : 'bi-chevron-right'" aria-hidden="true"></i>
            </button>

            <router-link
                class="po-sidebar__item"
                :class="{ active: inicioActive }"
                :to="inicioHref"
                :aria-label="leerNombre()"
                data-tooltip="Ir al inicio"
            >
                <i class="bi bi-shop-window" aria-hidden="true"></i>
                <span class="po-sidebar__label">Inicio</span>
            </router-link>

            <button
                v-if="!appOnline"
                type="button"
                class="po-sidebar__item po-sidebar__item--status"
                :data-tooltip="offlineStatusTooltip"
                :aria-label="offlineStatusTooltip"
                :disabled="forcingOfflineSync"
                @click="forzarSincronizacionOffline"
            >
                <i
                    class="bi"
                    :class="forcingOfflineSync ? 'bi-arrow-repeat po-sidebar__spin' : 'bi-wifi-off'"
                    aria-hidden="true"
                ></i>
                <span class="po-sidebar__label">{{ forcingOfflineSync ? 'Sincronizando…' : 'Sin conexión' }}</span>
                <span
                    v-if="offlinePendingBadge > 0 && !forcingOfflineSync"
                    class="po-sidebar__badge"
                    :title="offlinePendingBadge + ' pendiente(s) de sincronizar'"
                >{{ offlinePendingBadge > 99 ? '99+' : offlinePendingBadge }}</span>
            </button>

            <nav v-if="appOnline || pinnedItems.length" class="po-sidebar__section">
                <router-link
                    v-for="item in pinnedItems"
                    :key="'sv-' + item.id"
                    :to="item.href"
                    class="po-sidebar__item"
                    :class="{ 'po-sidebar__item': primaryCta && item.id === primaryCta.id }"
                    :data-tooltip="item.label"
                    :aria-label="item.label"
                >
                    <i class="bi" :class="item.icon" aria-hidden="true"></i>
                    <span class="po-sidebar__label">{{ item.label }}</span>
                    <span v-if="item.showPedidosBadge && pedidosNuevosBadge" class="po-sidebar__badge">{{ pedidosNuevosBadge }}</span>
                </router-link>
            </nav>

            <nav v-if="hasFlyouts" class="po-sidebar__section">
                <div
                    v-if="venderFlyoutItems.length"
                    class="po-sidebar__group"
                    @mouseenter="openSidebarGroup('vender')"
                    @mouseleave="scheduleCloseSidebarGroup"
                >
                    <button
                        type="button"
                        class="po-sidebar__item po-sidebar__item--trigger"
                        :class="{ 'po-sidebar__item--open': sidebarOpenGroup === 'vender', 'po-sidebar__item--active': venderFlyoutActive }"
                        data-tooltip="Vender"
                        aria-label="Vender"
                        aria-haspopup="true"
                        :aria-expanded="sidebarOpenGroup === 'vender' ? 'true' : 'false'"
                        @click="toggleSidebarGroup('vender')"
                    >
                        <i class="bi bi-cash-stack" aria-hidden="true"></i>
                        <span class="po-sidebar__label">Vender</span>
                    </button>
                    <div v-show="sidebarOpenGroup === 'vender'" class="po-sidebar__flyout">
                        <p class="po-sidebar__flyout-title">Vender</p>
                        <router-link
                            v-for="item in venderFlyoutItems"
                            :key="'svf-' + item.id"
                            :to="item.href"
                            class="po-sidebar__flyout-link"
                            @click="sidebarOpenGroup = null"
                        >
                            <i class="bi" :class="item.icon" aria-hidden="true"></i>
                            <span>{{ item.label }}</span>
                            <span v-if="item.showPedidosBadge && pedidosNuevosBadge" class="badge rounded-pill text-bg-danger ms-auto">{{ pedidosNuevosBadge }}</span>
                        </router-link>
                    </div>
                </div>

                <div
                    v-if="catalogoItems.length"
                    class="po-sidebar__group"
                    @mouseenter="openSidebarGroup('catalogo')"
                    @mouseleave="scheduleCloseSidebarGroup"
                >
                    <button
                        type="button"
                        class="po-sidebar__item po-sidebar__item--trigger"
                        :class="{ 'po-sidebar__item--open': sidebarOpenGroup === 'catalogo', 'po-sidebar__item--active': catalogoActive }"
                        data-tooltip="Catálogo"
                        aria-label="Catálogo"
                        aria-haspopup="true"
                        :aria-expanded="sidebarOpenGroup === 'catalogo' ? 'true' : 'false'"
                        @click="toggleSidebarGroup('catalogo')"
                    >
                        <i class="bi bi-collection" aria-hidden="true"></i>
                        <span class="po-sidebar__label">Catálogo</span>
                    </button>
                    <div v-show="sidebarOpenGroup === 'catalogo'" class="po-sidebar__flyout">
                        <p class="po-sidebar__flyout-title">Catálogo</p>
                        <router-link
                            v-for="item in catalogoItems"
                            :key="'sc-' + item.id"
                            :to="item.href"
                            class="po-sidebar__flyout-link"
                            @click="sidebarOpenGroup = null"
                        >
                            <i class="bi" :class="item.icon" aria-hidden="true"></i>
                            <span>{{ item.label }}</span>
                        </router-link>
                    </div>
                </div>

                <div
                    v-if="gestionItems.length"
                    class="po-sidebar__group"
                    @mouseenter="openSidebarGroup('gestion')"
                    @mouseleave="scheduleCloseSidebarGroup"
                >
                    <button
                        type="button"
                        class="po-sidebar__item po-sidebar__item--trigger"
                        :class="{ 'po-sidebar__item--open': sidebarOpenGroup === 'gestion', 'po-sidebar__item--active': gestionActive }"
                        data-tooltip="Gestión"
                        aria-label="Gestión"
                        aria-haspopup="true"
                        :aria-expanded="sidebarOpenGroup === 'gestion' ? 'true' : 'false'"
                        @click="toggleSidebarGroup('gestion')"
                    >
                        <i class="bi bi-gear" aria-hidden="true"></i>
                        <span class="po-sidebar__label">Gestión</span>
                    </button>
                    <div v-show="sidebarOpenGroup === 'gestion'" class="po-sidebar__flyout">
                        <p class="po-sidebar__flyout-title">Gestión</p>
                        <router-link
                            v-for="item in gestionItems"
                            :key="'sg-' + item.id"
                            :to="item.href"
                            class="po-sidebar__flyout-link"
                            @click="sidebarOpenGroup = null"
                        >
                            <i class="bi" :class="item.icon" aria-hidden="true"></i>
                            <span>{{ item.label }}</span>
                        </router-link>
                    </div>
                </div>
            </nav>

            <div class="po-sidebar__spacer"></div>

            <div class="po-sidebar__section">
                <router-link
                    v-if="cuentaLink"
                    class="po-sidebar__item po-sidebar__item--profile"
                    :to="cuentaLink.href"
                    :data-tooltip="cuentaLink.label"
                    :aria-label="cuentaLink.label"
                >
                    <i class="bi" :class="cuentaLink.icon" aria-hidden="true"></i>
                    <span class="po-sidebar__label">{{ cuentaLink.label }}</span>
                </router-link>
                <button
                    type="button"
                    class="po-sidebar__item"
                    data-tooltip="Cerrar sesión"
                    aria-label="Cerrar sesión"
                    data-bs-toggle="modal"
                    data-bs-target="#mainLogoutModal"
                >
                    <i class="bi bi-box-arrow-right" aria-hidden="true"></i>
                    <span class="po-sidebar__label">Cerrar sesión</span>
                </button>
            </div>
        </aside>

        <ConfirmLogoutModal modal-id="mainLogoutModal" @confirm="cerrarSesion" />
    </div>
</template>

<script>
import { RouterLink } from 'vue-router';
import router from '@/router';
import { getVendedorNombre, cerrarSesionPanel } from '@/utils/session';
import ConfirmLogoutModal from '@/components/shared/ConfirmLogoutModal.vue';
import Swal from 'sweetalert2';
import { hasFeature } from '@/utils/features';
import { connectivity, onConnectivityChange, refreshConnectivity } from '@/utils/connectivity';
import { esVentaPos } from '@/utils/pedidoOrigen';
import {
    getDesktopPrimaryCta,
    getDesktopFlyoutItems,
    getDesktopPinnedItems,
    getMobileBottomSlots,
    getVisibleNavItems,
} from '@/utils/panelNav';
import { ensureSidebarPinsLoaded, sidebarPinsState } from '@/utils/sidebarPins';
import {
    sidebarExpandState,
    toggleSidebarExpanded,
    syncSidebarExpandCss,
} from '@/utils/sidebarExpand';
import axios from 'axios';
import { pushModalGuard, popModalGuard, dismissModalGuard, clearModalGuardHistoryEntry } from '@/utils/modalBackGuard';
import {
    connectPedidosSocket,
    disconnectPedidosSocket,
    onNuevoPedido,
    playPedidoSound,
} from '@/utils/pedidosSocket';
import {
    countPendingOfflineNotifs,
    onPendingOfflineChange,
} from '@/utils/posOffline/pendingNotifs';
import { syncPendingOfflineWork } from '@/utils/posOffline/cache';

const MORE_SECTION_META = [
    { id: 'vender', title: 'Vender' },
    { id: 'catalogo', title: 'Catálogo' },
    { id: 'gestion', title: 'Gestión' },
    { id: 'cuenta', title: 'Cuenta' },
];

async function forzarSyncOfflineDesdeSidebar(usuario) {
    const online = await refreshConnectivity({ force: true });
    if (!online) {
        return { ok: false, reason: 'offline' };
    }
    if (usuario) {
        await syncPendingOfflineWork(usuario);
    }
    return { ok: true };
}
export default {
    components: {
        'router-link': RouterLink,
        ConfirmLogoutModal,
    },
    data() {
        return {
            pedidosNuevosBadge: 0,
            offlinePendingBadge: 0,
            socketOff: null,
            offPending: null,
            offConnectivity: null,
            moreOpen: false,
            moreGuardId: null,
            moreGuardHandled: false,
            sidebarOpenGroup: null,
            sidebarCloseTimer: null,
            forcingOfflineSync: false,
        };
    },
    computed: {
        sidebarExpanded() {
            return sidebarExpandState.expanded;
        },
        appOnline() {
            return connectivity.online;
        },
        offlineStatusTooltip() {
            if (this.forcingOfflineSync) {
                return 'Comprobando conexión y sincronizando…';
            }
            if (this.offlinePendingBadge > 0) {
                return `Sin conexión · ${this.offlinePendingBadge} pendiente(s). Tocá para forzar sincronización`;
            }
            return 'Sin conexión. Tocá para reintentar y sincronizar';
        },
        navOpts() {
            return {
                online: this.appOnline,
                usuario: this.leerUsuario(),
                pins: sidebarPinsState.pins,
            };
        },
        primaryCta() {
            return getDesktopPrimaryCta(this.navOpts);
        },
        pinnedItems() {
            return getDesktopPinnedItems(this.navOpts);
        },
        venderFlyoutItems() {
            return getDesktopFlyoutItems('vender', this.navOpts);
        },
        catalogoItems() {
            return getDesktopFlyoutItems('catalogo', this.navOpts);
        },
        gestionItems() {
            return getDesktopFlyoutItems('gestion', this.navOpts);
        },
        hasFlyouts() {
            return !!(this.venderFlyoutItems.length || this.catalogoItems.length || this.gestionItems.length);
        },
        cuentaLink() {
            const items = getVisibleNavItems({ ...this.navOpts, section: 'cuenta' })
                .filter((i) => i.id === 'perfil' || i.id === 'cuenta_vendedor');
            return items[0] || null;
        },
        venderFlyoutActive() {
            return this.venderFlyoutItems.some((i) => this.$route.path === i.href);
        },
        catalogoActive() {
            return this.catalogoItems.some((i) => this.$route.path === i.href);
        },
        gestionActive() {
            return this.gestionItems.some((i) => this.$route.path === i.href);
        },
        inicioHref() {
            return '/u/home';
        },
        inicioActive() {
            const path = this.$route.path;
            return path === '/u/home' || path.startsWith('/u/home/');
        },
        mobileBundle() {
            return getMobileBottomSlots(this.navOpts);
        },
        mobileSlots() {
            return this.mobileBundle.slots;
        },
        mobileMore() {
            return this.mobileBundle.more;
        },
        moreSections() {
            return MORE_SECTION_META.map((sec) => ({
                ...sec,
                items: this.mobileMore.filter((i) => i.section === sec.id),
            }));
        },
    },
    watch: {
        '$route.path'(path) {
            this.moreOpen = false;
            this.sidebarOpenGroup = null;
            if (path === '/u/pedidos') this.cargarPedidosNuevos();
        },
        moreOpen(open) {
            if (typeof document !== 'undefined') {
                document.body.style.overflow = open ? 'hidden' : '';
            }
            if (open) {
                if (this.moreGuardId == null) {
                    this.moreGuardId = pushModalGuard(() => {
                        this.moreGuardId = null;
                        this.moreOpen = false;
                    });
                }
                return;
            }
            if (this.moreGuardId != null && !this.moreGuardHandled) {
                popModalGuard(this.moreGuardId);
                this.moreGuardId = null;
            }
            this.moreGuardHandled = false;
        },
    },
    beforeUnmount() {
        if (this.socketOff) this.socketOff();
        if (this.offPending) this.offPending();
        if (this.offConnectivity) this.offConnectivity();
        // Al salir del panel (p. ej. logout) cortamos el socket compartido.
        disconnectPedidosSocket();
        if (this.moreGuardId != null) {
            popModalGuard(this.moreGuardId);
            this.moreGuardId = null;
        }
        if (typeof document !== 'undefined') document.body.style.overflow = '';
        if (this.sidebarCloseTimer) clearTimeout(this.sidebarCloseTimer);
        document.removeEventListener('click', this.onSidebarDocClick);
    },
    mounted() {
        document.addEventListener('click', this.onSidebarDocClick);
        syncSidebarExpandCss();
        ensureSidebarPinsLoaded();
        this.offPending = onPendingOfflineChange(() => {
            void this.refreshOfflinePendingBadge();
        });
        this.offConnectivity = onConnectivityChange(() => {
            void this.refreshOfflinePendingBadge();
        });
        void this.refreshOfflinePendingBadge();
        const u = this.leerUsuario();
        if (!u || u === 'admin') return;
        if (!this.appOnline) return;
        connectPedidosSocket(u);
        this.cargarPedidosNuevos();
        this.socketOff = onNuevoPedido((pedido) => {
            if (pedido && pedido.estado === 'nuevo') {
                this.pedidosNuevosBadge += 1;
            } else {
                this.pedidosNuevosBadge += 1;
            }
            if (this.$route.path !== '/u/pedidos') {
                playPedidoSound();
                Swal.fire({
                    toast: true,
                    position: 'top-end',
                    icon: 'info',
                    title: 'Nuevo pedido',
                    text: pedido && pedido.cliente_nombre ? pedido.cliente_nombre : 'Menú online',
                    showConfirmButton: false,
                    timer: 4000,
                });
            }
        });
    },
    methods: {
        feat(key) {
            return hasFeature(key);
        },
        cerrarSesion() {
            cerrarSesionPanel(router);
        },
        async refreshOfflinePendingBadge() {
            try {
                this.offlinePendingBadge = await countPendingOfflineNotifs(this.leerUsuario());
            } catch (_) {
                this.offlinePendingBadge = 0;
            }
        },
        async forzarSincronizacionOffline() {
            if (this.forcingOfflineSync) return;
            this.forcingOfflineSync = true;
            try {
                const result = await forzarSyncOfflineDesdeSidebar(this.leerUsuario());
                if (!result.ok) {
                    await Swal.fire({
                        toast: true,
                        position: 'top-end',
                        icon: 'warning',
                        title: 'Todavía sin conexión',
                        text: 'No se pudo alcanzar el servidor. Probá de nuevo en unos segundos.',
                        showConfirmButton: false,
                        timer: 3200,
                    });
                    return;
                }

                await this.refreshOfflinePendingBadge();

                await Swal.fire({
                    toast: true,
                    position: 'top-end',
                    icon: 'success',
                    title: 'Conexión restaurada',
                    text: 'Se forzó la sincronización de los cambios pendientes.',
                    showConfirmButton: false,
                    timer: 2800,
                });
            } catch (err) {
                console.error('Error al forzar sincronización offline:', err);
                await Swal.fire({
                    toast: true,
                    position: 'top-end',
                    icon: 'error',
                    title: 'No se pudo sincronizar',
                    text: 'Revisá la conexión e intentá otra vez.',
                    showConfirmButton: false,
                    timer: 3200,
                });
            } finally {
                this.forcingOfflineSync = false;
            }
        },
        toggleSidebarExpanded,
        openSidebarGroup(id) {
            if (this.sidebarCloseTimer) {
                clearTimeout(this.sidebarCloseTimer);
                this.sidebarCloseTimer = null;
            }
            this.sidebarOpenGroup = id;
        },
        scheduleCloseSidebarGroup() {
            if (this.sidebarCloseTimer) clearTimeout(this.sidebarCloseTimer);
            this.sidebarCloseTimer = setTimeout(() => {
                this.sidebarOpenGroup = null;
            }, 200);
        },
        toggleSidebarGroup(id) {
            this.sidebarOpenGroup = this.sidebarOpenGroup === id ? null : id;
        },
        onSidebarDocClick(e) {
            if (!this.sidebarOpenGroup) return;
            if (e.target.closest && e.target.closest('.po-sidebar__group')) return;
            this.sidebarOpenGroup = null;
        },
        cerrarMore() {
            this.moreOpen = false;
        },
        onMoreLinkClick(event, item) {
            const dest = item && item.href;
            this.moreGuardHandled = true;
            if (this.moreGuardId != null) {
                dismissModalGuard(this.moreGuardId);
                this.moreGuardId = null;
            }
            clearModalGuardHistoryEntry();
            this.moreOpen = false;
            if (!dest || this.$route.path === dest) {
                event.preventDefault();
            }
        },
        mobileIcon(item) {
            if (item.id === 'home') return 'bi-house-door-fill';
            if (item.id === 'productos') return 'bi-grid-3x3-gap-fill';
            if (item.id === 'pos') return 'bi-cash-coin';
            return item.icon;
        },
        async cargarPedidosNuevos() {
            const u = this.leerUsuario();
            if (!u) return;
            try {
                const { data } = await axios.get('/pedidos', { params: { usuario: u, limit: 100 } });
                const list = Array.isArray(data) ? data : [];
                this.pedidosNuevosBadge = list.filter(
                    (p) => p.estado === 'nuevo' && !esVentaPos(p)
                ).length;
            } catch (_) {
                /* ignore */
            }
        },
        leerNombre() {
            const v = getVendedorNombre();
            if (v) return v;
            return localStorage.getItem('nombre') || 'Mi negocio';
        },
        leerUsuario() {
            return localStorage.getItem('usuario') || '';
        },
    },
};
</script>

<style scoped>
.po-dash-nav-wrap {
    position: relative;
}

/* Estilos base del sidebar (.po-sidebar*) compartidos con NavbarAdmin.vue: ver global-ui.css */

.po-bottom-nav {
    display: none;
}

.po-nav-more {
    display: none;
}

@media screen and (max-width: 991.98px) {
    .po-bottom-nav {
        display: flex;
        position: fixed;
        bottom: 0;
        left: 0;
        right: 0;
        z-index: 1040;
        background: #0f172a;
        border-top: 1px solid rgba(255, 255, 255, 0.08);
        padding: 0.35rem 0.25rem calc(0.35rem + env(safe-area-inset-bottom, 0));
        justify-content: space-around;
        align-items: center;
    }

    .po-bottom-nav__item {
        position: relative;
        flex: 1;
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 0.15rem;
        text-decoration: none;
        color: #94a3b8;
        font-size: 0.65rem;
        font-weight: 500;
        padding: 0.25rem;
        min-width: 0;
        transition: color 0.2s ease;
        background: transparent;
        border: 0;
        cursor: pointer;
        font-family: inherit;
    }

    .po-bottom-nav__item--accent {
        color: #86efac;
    }

    .po-bottom-nav__item--open {
        color: #fff;
    }

    .po-nav-badge {
        position: absolute;
        top: 2px;
        right: 8px;
        min-width: 1.1rem;
        height: 1.1rem;
        padding: 0 0.25rem;
        border-radius: 999px;
        background: #dc3545;
        color: #fff;
        font-size: 0.65rem;
        font-weight: 700;
        line-height: 1.1rem;
        text-align: center;
    }

    .po-bottom-nav__item:hover,
    .po-bottom-nav__item.active {
        color: #fff;
    }

    .po-bottom-nav__ico {
        font-size: 1.35rem;
        line-height: 1;
    }

    .po-nav-more {
        display: block;
        position: fixed;
        inset: 0;
        z-index: 2000;
        pointer-events: auto;
    }

    .po-nav-more__backdrop {
        position: absolute;
        inset: 0;
        z-index: 0;
        border: 0;
        background: rgba(15, 23, 42, 0.55);
        cursor: pointer;
        pointer-events: auto;
    }

    .po-nav-more__sheet {
        position: absolute;
        left: 0;
        right: 0;
        bottom: 0;
        z-index: 1;
        pointer-events: auto;
        max-height: min(72vh, 560px);
        overflow: auto;
        background: #0f172a;
        border-radius: 18px 18px 0 0;
        padding: 0.85rem 1rem calc(4.75rem + env(safe-area-inset-bottom, 0));
        box-shadow: 0 -12px 40px rgba(0, 0, 0, 0.35);
        color: #e2e8f0;
    }

    .po-nav-more__head {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 0.75rem;
        margin-bottom: 0.75rem;
    }

    .po-nav-more__title {
        margin: 0;
        font-size: 1rem;
        font-weight: 700;
        color: #fff;
    }

    .po-nav-more__section {
        margin: 0.85rem 0 0.4rem;
        font-size: 0.68rem;
        font-weight: 700;
        letter-spacing: 0.06em;
        text-transform: uppercase;
        color: #94a3b8;
    }

    .po-nav-more__section:first-child {
        margin-top: 0;
    }

    .po-nav-more__grid {
        display: grid;
        grid-template-columns: repeat(2, minmax(0, 1fr));
        gap: 0.45rem;
    }

    .po-nav-more__link {
        display: flex;
        align-items: center;
        gap: 0.55rem;
        padding: 0.7rem 0.75rem;
        border-radius: 12px;
        background: rgba(255, 255, 255, 0.06);
        color: #e2e8f0;
        text-decoration: none;
        font-size: 0.84rem;
        font-weight: 600;
        cursor: pointer;
        -webkit-tap-highlight-color: transparent;
    }

    .po-nav-more__link i {
        font-size: 1.1rem;
        color: #93c5fd;
        flex-shrink: 0;
    }

    .po-nav-more__link:hover,
    .po-nav-more__link.active {
        background: rgba(255, 255, 255, 0.12);
        color: #fff;
    }

    .po-nav-more__foot {
        margin-top: 1rem;
        padding-top: 0.85rem;
        border-top: 1px solid rgba(255, 255, 255, 0.1);
    }

    .po-nav-more__logout {
        display: flex;
        align-items: center;
        gap: 0.55rem;
        width: 100%;
        padding: 0.75rem 0.85rem;
        border: 0;
        border-radius: 12px;
        background: rgba(239, 68, 68, 0.12);
        color: #fca5a5;
        font-size: 0.9rem;
        font-weight: 600;
        cursor: pointer;
        -webkit-tap-highlight-color: transparent;
    }

    .po-nav-more__logout i {
        font-size: 1.1rem;
        flex-shrink: 0;
    }

    .po-nav-more__logout:hover,
    .po-nav-more__logout:active {
        background: rgba(239, 68, 68, 0.2);
        color: #fecaca;
    }
}
</style>
